import React, { useMemo, useState } from 'react';
import dayjs from 'dayjs';
import { Alert, Button, Checkbox, DatePicker, Form, Input, Modal, Select, Space, Switch, Typography, Upload, message } from 'antd';
import {
    ITEM_CANVAS,
    ITEM_MAX_LAYERS,
    ITEM_SLOT_TRAIT,
    defaultIdentity,
    itemValue,
    sanitizeItemSvg,
} from '@designwithfriends/avatar';
import { avatarItemsApi } from '../services/catalogServiceAPI/avatarItemsProxy';
import { DesignerAvatar } from '../common/designerAvatar';

const { Text } = Typography;

export const SLOT_LABELS = { headwear: 'Headwear', accessory: 'Accessory' };
export const UNLOCK_LABELS = {
    free: 'Free for everyone',
    timed: 'Limited time (claim to keep)',
    reward: 'Challenge prize',
};
const UNLOCK_HELP = {
    free: 'Everyone can wear it for as long as it is enabled.',
    timed: 'Anyone who saves their avatar wearing it during the window keeps it for good. Afterwards nobody new can get it.',
    reward: 'Only users who win it can wear it. Choose it in a challenge\'s rewards.',
};
const POSITION_OPTIONS = [
    { value: 'front', label: 'In front of the designer' },
    { value: 'back', label: 'Behind the designer' },
];
const PREVIEW_ID = '00000000-0000-4000-8000-000000000000';

// A spread of real cases: short and big hair, glasses, a built-in accessory and a suit.
const SAMPLE_AVATARS = [
    ...Array.from({ length: 8 }, (_, i) => defaultIdentity(`item-sample-${i}`).avatar),
    { ...defaultIdentity('item-sample-8').avatar, top: 'fro' },
    { ...defaultIdentity('item-sample-9').avatar, top: 'none' },
    { ...defaultIdentity('item-sample-10').avatar, clothing: 'suit' },
    { ...defaultIdentity('item-sample-11').avatar, top: 'bun', glasses: 'wayfarers' },
];

/** Create or edit one avatar item, with a live preview on sample avatars wearing it. */
export function ItemForm({ item, onClose, onSaved }) {
    const isNew = !item.id;
    const [name, setName] = useState(item.name ?? '');
    const [slot, setSlot] = useState(item.slot ?? 'headwear');
    const [hidesHair, setHidesHair] = useState(item.hidesHair ?? false);
    const [unlock, setUnlock] = useState(item.unlock ?? 'timed');
    const [range, setRange] = useState(item.availableFrom ? [dayjs(item.availableFrom), dayjs(item.availableUntil)] : null);
    const [enabled, setEnabled] = useState(item.enabled ?? false);
    const [layers, setLayers] = useState(item.layers ?? []);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);

    const previewItems = useMemo(() => new Map([[PREVIEW_ID, {
        id: PREVIEW_ID,
        name,
        slot,
        hidesHair: slot === 'headwear' && hidesHair,
        unlock: 'free',
        availableFrom: null,
        availableUntil: null,
        layers,
        version: JSON.stringify([slot, hidesHair, layers]),
    }]]), [name, slot, hidesHair, layers]);

    const addLayer = async (file) => {
        try {
            const svg = sanitizeItemSvg(await file.text());
            setLayers((current) => [...current, { position: 'front', svg }]);
        } catch (e) {
            message.error(`${file.name}: ${e.message}`);
        }
    };

    const setPosition = (index, position) =>
        setLayers((current) => current.map((layer, i) => (i === index ? { ...layer, position } : layer)));

    const save = async () => {
        if (!name.trim()) {
            setError('Name is required');
            return;
        }
        if (unlock === 'timed' && !range) {
            setError('A limited-time item needs its window');
            return;
        }
        const body = {
            name,
            slot,
            hidesHair,
            unlock,
            availableFrom: unlock === 'timed' ? range[0].toISOString() : null,
            availableUntil: unlock === 'timed' ? range[1].toISOString() : null,
            enabled,
            layers,
        };
        setSaving(true);
        setError(null);
        try {
            if (isNew) await avatarItemsApi.create(body);
            else await avatarItemsApi.update(item.id, body);
            onSaved();
        } catch (e) {
            setError(e.message);
            setSaving(false);
        }
    };

    return (
        <Modal
            open
            width={760}
            title={isNew ? 'New avatar item' : `Edit "${item.name}"`}
            okText="Save"
            confirmLoading={saving}
            onOk={save}
            onCancel={onClose}
        >
            <Form layout="vertical">
                <Form.Item label="Name" required>
                    <Input value={name} maxLength={80} onChange={(e) => setName(e.target.value)} placeholder="Santa hat" />
                </Form.Item>
                <Form.Item label="Worn as" extra="It takes the place of the built-in headwear or accessory while worn.">
                    <Space>
                        <Select
                            value={slot}
                            onChange={setSlot}
                            options={Object.entries(SLOT_LABELS).map(([value, label]) => ({ value, label }))}
                            style={{ width: 160 }}
                        />
                        {slot === 'headwear' && (
                            <Checkbox checked={hidesHair} onChange={(e) => setHidesHair(e.target.checked)}>Hide the hair under it</Checkbox>
                        )}
                    </Space>
                </Form.Item>
                <Form.Item label="Unlocked by" extra={UNLOCK_HELP[unlock]}>
                    <Select
                        value={unlock}
                        onChange={setUnlock}
                        options={Object.entries(UNLOCK_LABELS).map(([value, label]) => ({ value, label }))}
                        style={{ width: 280 }}
                    />
                </Form.Item>
                {unlock === 'timed' && (
                    <Form.Item label="Claim window (your local time)" required>
                        <DatePicker.RangePicker showTime value={range} onChange={setRange} />
                    </Form.Item>
                )}
                <Form.Item label="Enabled" extra="A disabled item is hidden from everyone, owners included.">
                    <Switch checked={enabled} onChange={setEnabled} />
                </Form.Item>
                <Form.Item
                    label="Layers"
                    required
                    extra={`SVG files drawn on a ${ITEM_CANVAS.width}×${ITEM_CANVAS.height} canvas (viewBox="0 0 ${ITEM_CANVAS.width} ${ITEM_CANVAS.height}"), plain vector shapes only, up to 20 KB each. The head is centered on (132, 92) with radius 56; the background circle on (132, 160) with radius 120.`}
                >
                    <Space direction="vertical" style={{ width: '100%' }}>
                        {layers.map((layer, index) => (
                            <Space key={index}>
                                <Text>Layer {index + 1}</Text>
                                <Select value={layer.position} options={POSITION_OPTIONS} onChange={(value) => setPosition(index, value)} style={{ width: 220 }} />
                                <Text type="secondary">{(layer.svg.length / 1024).toFixed(1)} KB</Text>
                                <Button size="small" danger onClick={() => setLayers((current) => current.filter((_, i) => i !== index))}>Remove</Button>
                            </Space>
                        ))}
                        {layers.length < ITEM_MAX_LAYERS && (
                            <Upload accept=".svg,image/svg+xml" showUploadList={false} beforeUpload={(file) => { addLayer(file); return false; }}>
                                <Button>Upload SVG layer</Button>
                            </Upload>
                        )}
                    </Space>
                </Form.Item>
            </Form>
            <Text strong>Preview</Text>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(6, 1fr)', gap: 12, marginTop: 8, justifyItems: 'center' }}>
                {SAMPLE_AVATARS.map((avatar, index) => (
                    <DesignerAvatar
                        key={index}
                        avatar={{ ...avatar, [ITEM_SLOT_TRAIT[slot]]: itemValue(PREVIEW_ID) }}
                        items={previewItems}
                        size={96}
                    />
                ))}
            </div>
            {error && <Alert type="error" message={error} style={{ marginTop: 16 }} />}
        </Modal>
    );
}
